import { lazy, Suspense, useCallback, useEffect } from "react";
import { Route, Routes, Navigate, useLocation, useNavigate } from "react-router-dom";
import PublicRoute from "./PublicRoute";
import PrivateRoute from "./PrivateRoute";
import { useRecoilValue, useResetRecoilState } from "recoil";
import {
  isAuthenticated,
  userData,
  navigationLayoutMode,
  accessToken,
  idToken,
  refreshToken,
  portfolioTreeStore,
} from "../store";
import NavbarSelector from "../features/navbar/NavbarSelector";
import Footer from "@/components/Footer";
import { Auth } from "aws-amplify";
import { triggerSnackbar } from "@/components/toastMessage";
import AuthSessionRefresher from "@/auth/AuthSessionRefresher";
import UserBlockedDialog from "@/auth/UserBlockedDialog";
import SessionExpiredContainer from "@/auth/SessionExpiredContainer";
import { pendingRequestQueue } from "@/services/axiosInterceptor";
import MobileVerificationReminder from "@/features/profile/MobileVerificationReminder";

import {
  AccountingMenuFlag,
  DirectoriesMenuFlag,
  DocumentsMenuFlag,
  ImportMenuFlag,
  MaintenanceMenuFlag,
  NotificationsMenuFlag,
  PortfoliosMenuFlag,
  // UnitsMenuFlag,
  ListingsMenuFlag,
  ReportsMenuFlag,
  TasksMenuFlag,
} from "../featureFlags/mainMenuFlags";

import {
  ChartOfAccountsMenuFlag,
  BatchChargesMenuFlag,
  ConnectedBankAccountsMenuFlag,
  BankTransactionsMenuFlag,
  CompanyFinancialsMenuFlag,
  DisputesMenuFlag,
} from "../featureFlags/accountingFeatureFalgs";

import {
  TenantDirectoryMenuFlag,
  UsersDirectoryMenuFlag,
  VendorDirectoryMenuFlag,
  OwnersDirectoryMenuFlag,
  MaintenanceTechDirectoryMenuFlag,
} from "../featureFlags/directoriesFeatureFlags";
import { YvooaSpinner } from "@/components/loaders/yvooaSpinner";
import { isAsstPropertyManager, isPropertyManager } from "../auth/accessUtils";

// Lazy-loaded components
const Landing = lazy(() =>
  import("../features/landing/LandingPage").then((module) => ({
    default: module.default,
  })),
);
// Enhanced auth components from features/auth
const Login = lazy(() => import("../features/auth/LoginPage"));
const SignUp = lazy(() => import("../features/auth/SignUpPage"));
const ForgotPass = lazy(() => import("../features/auth/ForgotPasswordPage"));
// Survey component from features/survey
const Survey = lazy(() => import("../features/survey/SurveyPage"));

// TempUser moved to features/auth
const TempUser = lazy(() => import("../features/auth/TempUserPage"));
const BankSuccessPage = lazy(
  () => import("../features/accounting/connectedBankAccounts/BankSuccessPage"),
);

// Subscription components from features/subscription
const SubscriptionPlans = lazy(
  () => import("../features/subscription/SubscriptionPlansPage"),
);
const SubscriptionSuccess = lazy(
  () => import("../features/subscription/SubscriptionSuccessPage"),
);
const SubscriptionFailed = lazy(
  () => import("../features/subscription/SubscriptionFailedPage"),
);
const SubscriptionDetails = lazy(
  () => import("../features/subscription/SubscriptionDetailsPage"),
);

const VendorDashboard = lazy(
  () => import("../features/vendorDirectory/VendorDashboardPage"),
);
const PortfolioDashboard = lazy(() =>
  import("../features/portfolios/portfolioDashboard/portfoliosDashboard").then(
    (module) => ({
      default: module.PortfolioDashboard,
    }),
  ),
);
const PropertyTable = lazy(
  () => import("../features/portfolioDetails/PortfolioDetailsPage"),
);
const Dashboard = lazy(() => import("../features/dashboard/Dashboard"));
const PropertyDetails = lazy(
  () => import("../features/propertyDetails/PropertyDetailsPage"),
);
const UnitDetails = lazy(
  () => import("../features/unitDetails/UnitDetailsPage"),
);
const InspectionDetailsPage = lazy(
  () => import("../features/inspections/InspectionDetailsPage"),
);
const MaintenanceRequestDetails = lazy(
  () =>
    import("../features/maintenanceRequests/maintenanceDetails/MaintenanceDetailsPage"),
);
const UserTable = lazy(
  () => import("../features/userDirectory/UserDirectoryPage"),
);
const VendorDetails = lazy(
  () => import("../features/vendorDirectory/VendorDetailsPage"),
);
const MaintenanceDashboardPage = lazy(() =>
  import("../features/Maintenance/maintenanceDashboard").then((module) => ({
    default: module.MaintenanceDashboardPage,
  })),
);
const ImportCsv = lazy(() => import("../features/importFiles/ImportCsvPage"));
const MyProfile = lazy(() => import("../features/profile/ProfilePage"));
const Documents = lazy(() => import("../features/documents/DocumentsPage"));
const TenantDetails = lazy(
  () => import("../features/tenantDirectory/details/TenantDetailsPage"),
);
const CompanyFinancials = lazy(() =>
  import("../features/accounting/companyFinancials/CompanyFinancialsDashboard").then(
    (module) => ({ default: module.CompanyFinancialsDashboard }),
  ),
);
const BankInfo = lazy(() =>
  import("../features/accounting/connectedBankAccounts/ConnectedBankAccountsDashboard").then(
    (module) => ({
      default: module.ConnectedBankAccountsDashboard,
    }),
  ),
);
const BankDashboard = lazy(
  () =>
    import("../features/accounting/connectedBankAccounts/viewConnectedBankAccountDetails"),
);
const Notifications = lazy(
  () => import("../features/notifications/NotificationsPage"),
);
const OwnerDashboard = lazy(
  () => import("../features/ownerDirectory/OwnerDashboardPage"),
);
const OwnerDetails = lazy(
  () => import("../features/ownerDirectory/OwnerDetailsPage"),
);
const LeaseDetails = lazy(
  () => import("../features/tenantDirectory/details/lease/leaseDetails.jsx"),
);
const ManageNotification = lazy(
  () => import("../features/notifications/ManageNotificationsPage"),
);
const EvictionDetails = lazy(
  () =>
    import("../features/tenantDirectory/details/eviction/evictionDetails.jsx"),
);

const Accounting = lazy(
  () => import("../features/accounting/bankTransactions/BankTransactionsPage"),
);
const ChartofAccounts = lazy(
  () => import("../features/accounting/chartOfAccounts/ChartofAccounts"),
);
const AccountDetails = lazy(
  () => import("../features/accounting/chartOfAccounts/AccountDetails"),
);
const TransactionDetails = lazy(
  () => import("../features/accounting/chartOfAccounts/TransactionDetails"),
);
const BatchChargesTable = lazy(
  () => import("../features/accounting/batchCharges/BatchChargesTablePage"),
);
const BatchChargeDetails = lazy(
  () => import("../features/accounting/batchCharges/BatchChargeDetailsPage"),
);
const CustomerDirectory = lazy(() =>
  import("../features/accounting/customerDirectory/CustomerDirDashboard").then(
    (module) => ({ default: module.CustomerDirDashboardPage }),
  ),
);
const CustomerDetails = lazy(() =>
  import("../features/accounting/customerDirectory/CustomerDetailsPage").then(
    (module) => ({ default: module.CustomerDetailsPage }),
  ),
);
const PayBillsDashboard = lazy(() =>
  import("../features/accounting/payBills/PayBillsDashboard").then(
    (module) => ({ default: module.PayBillsDashboardPage }),
  ),
);
const DisputesPage = lazy(() => import("../features/disputes/DisputesPage"));

const VacateNoticeDetails = lazy(
  () =>
    import("../features/tenantDirectory/details/vacateNotice/vacateNoticeDetails.jsx"),
);
const ListingDashboard = lazy(
  () => import("../features/listings/Listing/listingDashboard.jsx"),
);
const ProspectsDashboard = lazy(
  () => import("../features/listings/Prospect/prospectsDashboard.jsx"),
);
const IntakeFormsDashboard = lazy(
  () => import("../features/listings/IntakeForms/intakeFormsDashboard.jsx"),
);
const TasksDirectory = lazy(() =>
  import("../features/tasks/tasksOverview").then((module) => ({
    default: module.TasksOverview,
  })),
);
const ListingDetails = lazy(
  () => import("../features/listings/Listing/listingDetails.jsx"),
);
const UpdateListing = lazy(
  () => import("../features/listings/Listing/updateListing.jsx"),
);
const ListingsLayout = lazy(
  () => import("../features/listings/listingMenu.js"),
);
const MaintenanceTechDashboard = lazy(
  () =>
    import("../features/maintenanceTechDirectory/MaintenanceTechDashboardPage"),
);
const MaintenanceTechDetails = lazy(
  () =>
    import("../features/maintenanceTechDirectory/MaintenanceTechDetailsPage"),
);
const PreviewListing = lazy(
  () => import("../features/listings/Listing/previewListing.jsx"),
);
const StatementsDashboardPage = lazy(() =>
  import("../features/reports/statementDirectory/StatementsDashboardPage").then(
    (module) => ({ default: module.StatementsDashboardPage }),
  ),
);
const PortfolioStatementsPage = lazy(() =>
  import("../features/reports/statementDirectory/PortfolioStatementsPage").then(
    (module) => ({ default: module.PortfolioStatementsPage }),
  ),
);

const ReportLayoutPage = lazy(() =>
  import("../features/reports/reportLayout").then((module) => ({
    default: module.ReportLayout,
  })),
);

const TenantsDashboard = lazy(() =>
  import("../features/tenantDirectory/tenantDirectoryDashboardPage").then(
    (module) => ({
      default: module.TenantDirectoryDashboardPage,
    }),
  ),
);

const UnitDashboard = lazy(() =>
  import("../features/unitDirectory/unitsDashboardPage").then((module) => ({
    default: module.UnitsDashboard,
  })),
);

const IntakeFormDetailsPage = lazy(
  () => import("../features/listings/IntakeForms/intakeFormDetailsPage.jsx"),
);

const ProvisioningPage = lazy(
  () => import("../features/provisioning/ProvisioningPage"),
);

const NAVBAR_HEIGHT = "4rem"; // 16px (h-16) = 4rem for mobile, matches navbar

export default function AppRoutes() {
  const userAuthenticated = useRecoilValue(isAuthenticated);
  const userInfo = useRecoilValue(userData);
  const resetUser = useResetRecoilState(userData);
  const resetAccessToken = useResetRecoilState(accessToken);
  const resetIdToken = useResetRecoilState(idToken);
  const resetRefreshToken = useResetRecoilState(refreshToken);
  const resetPortfolioTree = useResetRecoilState(portfolioTreeStore);
  const navLayout = useRecoilValue(navigationLayoutMode);
  const location = useLocation();
  const navigate = useNavigate();

  const handleForcedLogout = useCallback(async () => {
    try {
      await Auth.signOut();
    } catch (error) {
      console.error("Error signing out:", error);
      triggerSnackbar("Failed to sign out", "error");
    } finally {
      pendingRequestQueue.reset();
      resetUser();
      resetAccessToken();
      resetIdToken();
      resetRefreshToken();
      resetPortfolioTree();
      sessionStorage.clear();
      navigate("/login", { replace: true });
    }
  }, [
    navigate,
    resetAccessToken,
    resetIdToken,
    resetPortfolioTree,
    resetRefreshToken,
    resetUser,
  ]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const hideNavbarPaths = [
    "/survey",
    "/subscription",
    "/subscription/success",
    "/subscription/retry",
    "/version",
  ];
  const showNavbar = !hideNavbarPaths.includes(location.pathname);
  const routesContent = (
    <Suspense fallback={<YvooaSpinner />}>
      <Routes>
        {/* Primary Pages */}
        <Route path="/" exact element={<PublicRoute child={<Landing />} />} />
        <Route
          path="/login"
          exact
          element={userAuthenticated ? <Navigate to="/dashboard" /> : <Login />}
        />

        <Route
          path="/signup"
          exact
          element={<PublicRoute child={<SignUp />} />}
        />

        <Route
          path="/forgotpassword"
          exact
          element={<PublicRoute child={<ForgotPass />} />}
        />

        <Route
          path="/survey"
          exact
          element={
            userInfo.surveyStatus === true ? (
              <Navigate to="/dashboard" />
            ) : (
              <PrivateRoute child={<Survey />} />
            )
          }
        />

        {/* Subscription */}
        {!isPropertyManager(userInfo?.userRole) &&
          !isAsstPropertyManager(userInfo?.userRole) && (
            <Route path="/subscription">
              <Route
                index
                element={<PrivateRoute child={<SubscriptionPlans />} />}
              />
              <Route
                path="success"
                element={<PrivateRoute child={<SubscriptionSuccess />} />}
              />
              <Route
                path="retry"
                element={<PrivateRoute child={<SubscriptionFailed />} />}
              />
              <Route
                path="Details"
                element={<PrivateRoute child={<SubscriptionDetails />} />}
              />
            </Route>
          )}

        <Route
          path="/tempuser"
          exact
          element={<PublicRoute child={<TempUser />} />}
        />
        <Route
          path="/bankSuccess"
          exact
          element={<PublicRoute child={<BankSuccessPage />} />}
        />

        <Route
          path="/dashboard"
          element={<PrivateRoute child={<Dashboard />} />}
        />

        {/* Portfolio */}
        {PortfoliosMenuFlag() && (
          <>
            <Route
              path="/portfolios"
              element={<PrivateRoute child={<PortfolioDashboard />} />}
            />
            <Route path="/portfolios">
            <Route
              path="properties"
              element={<PrivateRoute child={<PropertyTable />} />}
            />
              <Route
                path="propertyDetails"
                element={<PrivateRoute child={<PropertyDetails />} />}
              />
              <Route
                path="unitDetails"
                element={<PrivateRoute child={<UnitDetails />} />}
              />
            </Route>
          </>
        )}

        {/* Units */}
        <Route path="/units">
          <Route index element={<PrivateRoute child={<UnitDashboard />} />} />
        </Route>

        <Route
          path="/inspections/details"
          element={<PrivateRoute child={<InspectionDetailsPage />} />}
        />

        {/* Tenants */}
        {TenantDirectoryMenuFlag() && (
          <Route path="/directories/tenants">
            <Route
              path="dashboard"
              element={<PrivateRoute child={<TenantsDashboard />} />}
            />
            <Route
              path="tenantDetails"
              element={<PrivateRoute child={<TenantDetails />} />}
            />
            <Route
              path="leaseDetails"
              element={<PrivateRoute child={<LeaseDetails />} />}
            />
            <Route
              path="evictionDetails"
              element={<PrivateRoute child={<EvictionDetails />} />}
            />
            <Route
              path="vacateNoticeDetails"
              element={<PrivateRoute child={<VacateNoticeDetails />} />}
            />
          </Route>
        )}

        {/* Maintenance */}
        {MaintenanceMenuFlag() && (
          <Route path="/maintenance">
            <Route
              index
              element={<PrivateRoute child={<MaintenanceDashboardPage />} />}
            />
            <Route
              path="maintenanceDetails"
              element={<PrivateRoute child={<MaintenanceRequestDetails />} />}
            />
          </Route>
        )}

        {/* Vendor */}
        {VendorDirectoryMenuFlag() && (
          <Route path="/directories/vendor">
            <Route
              index
              element={<PrivateRoute child={<VendorDashboard />} />}
            />
            <Route
              path="vendorDetails"
              element={<PrivateRoute child={<VendorDetails />} />}
            />
          </Route>
        )}

        {/* Reports */}
        {ReportsMenuFlag() && (
          <Route path="/reports">
            <Route
              index
              element={<PrivateRoute child={<ReportLayoutPage />} />}
            />
            <Route
              path="statements"
              element={<PrivateRoute child={<StatementsDashboardPage />} />}
            />
            <Route
              path="statements/:portfolioId"
              element={<PrivateRoute child={<PortfolioStatementsPage />} />}
            />
          </Route>
        )}

        {/* Documents */}
        {DocumentsMenuFlag() && (
          <Route
            path="/documents"
            element={<PrivateRoute child={<Documents />} />}
          />
        )}

        {/* Import */}
        {ImportMenuFlag() && (
          <Route
            path="/importCSV"
            element={<PrivateRoute child={<ImportCsv />} />}
          />
        )}

        {/* Directories */}
        {DirectoriesMenuFlag() && (
          <Route path="/directories">
            {/* Users */}
            {UsersDirectoryMenuFlag() && (
              <Route
                path="users"
                element={<PrivateRoute child={<UserTable />} />}
              />
            )}
            {/* Owners */}
            {OwnersDirectoryMenuFlag() && (
              <Route path="owners">
                <Route
                  index
                  element={<PrivateRoute child={<OwnerDashboard />} />}
                />
                <Route
                  path="ownerDetails"
                  element={<PrivateRoute child={<OwnerDetails />} />}
                />
              </Route>
            )}

            {/* MaintenanceTech */}
            {MaintenanceTechDirectoryMenuFlag() && (
              <Route path="maintenanceTech">
                <Route
                  index
                  element={
                    <PrivateRoute child={<MaintenanceTechDashboard />} />
                  }
                />
                <Route
                  path="techDetails"
                  element={<PrivateRoute child={<MaintenanceTechDetails />} />}
                />
              </Route>
            )}
          </Route>
        )}

        {/* Vendor */}
        {VendorDirectoryMenuFlag() && (
          <Route path="vendor">
            <Route
              index
              element={<PrivateRoute child={<VendorDashboard />} />}
            />
            <Route
              path="vendorDetails"
              element={<PrivateRoute child={<VendorDetails />} />}
            />
          </Route>
        )}

        {/* Notifications */}
        {NotificationsMenuFlag() && (
          <Route path="notifications">
            <Route index element={<PrivateRoute child={<Notifications />} />} />
            <Route
              path="manageNotification"
              element={<PrivateRoute child={<ManageNotification />} />}
            />
          </Route>
        )}

        {/* Accounting */}
        {AccountingMenuFlag() && (
          <Route path="/accounting">
            {/* <Route index element={<PrivateRoute child={<Accounting />} />} /> */}
            {CompanyFinancialsMenuFlag() && (
              <Route
                path="companyFinancials"
                element={<PrivateRoute child={<CompanyFinancials />} />}
              />
            )}
            {ChartOfAccountsMenuFlag() && (
              <Route path="chartOfAccounts">
                <Route
                  index
                  element={<PrivateRoute child={<ChartofAccounts />} />}
                />
                <Route
                  path="details"
                  element={<PrivateRoute child={<AccountDetails />} />}
                />
                <Route
                  path="transactionDetails"
                  element={<PrivateRoute child={<TransactionDetails />} />}
                />
              </Route>
            )}
            {ConnectedBankAccountsMenuFlag() && (
              <>
                <Route
                  path="connectedBankAccounts"
                  element={<PrivateRoute child={<BankInfo />} />}
                />

                <Route
                  path="connectedAccountDetails"
                  element={<PrivateRoute child={<BankDashboard />} />}
                />
              </>
            )}
            {BankTransactionsMenuFlag() && (
              <Route
                path="bankTransactions"
                element={<PrivateRoute child={<Accounting />} />}
              />
            )}
            {BatchChargesMenuFlag() && (
              <Route
                path="batchCharges"
                element={<PrivateRoute child={<BatchChargesTable />} />}
              />
            )}
            {BatchChargesMenuFlag() && (
              <Route
                path="batchCharge/Details"
                element={<PrivateRoute child={<BatchChargeDetails />} />}
              />
            )}
            {AccountingMenuFlag() && (
              <Route
                path="customerDirectory"
                element={<PrivateRoute child={<CustomerDirectory />} />}
              />
            )}
            {AccountingMenuFlag() && (
              <Route
                path="customerDetails"
                element={<PrivateRoute child={<CustomerDetails />} />}
              />
            )}
            {AccountingMenuFlag() && (
              <Route
                path="payBills"
                element={<PrivateRoute child={<PayBillsDashboard />} />}
              />
            )}
            {DisputesMenuFlag() && (
              <Route
                path="disputes"
                element={<PrivateRoute child={<DisputesPage />} />}
              />
            )}
          </Route>
        )}

        {/* Tasks */}
        {TasksMenuFlag() && (
          <Route path="/tasks">
            <Route
              index
              element={<PrivateRoute child={<TasksDirectory />} />}
            />
          </Route>
        )}

        {/* Listings */}
        {ListingsMenuFlag() && (
          <Route element={<PrivateRoute child={<ListingsLayout />} />}>
            <Route path="/listings">
              <Route index element={<ListingDashboard />} />
              <Route path="details" element={<ListingDetails />} />
              <Route path="update" element={<UpdateListing />} />
              <Route path="preview" element={<PreviewListing />} />
              <Route path="prospects" element={<ProspectsDashboard />} />
            </Route>
          </Route>
        )}

        {/* Intake Forms */}
        {ListingsMenuFlag() && (
          <Route path="/intakeForms">
            <Route
              index
              element={<PrivateRoute child={<IntakeFormsDashboard />} />}
            />
            <Route
              path="details"
              element={<PrivateRoute child={<IntakeFormDetailsPage />} />}
            />
          </Route>
        )}

        {/* Provisioning */}
        <Route
          path="/provisioning"
          element={<PrivateRoute child={<ProvisioningPage />} />}
        />

        {/* Routines */}
        <Route
          path="/myProfile"
          element={<PrivateRoute child={<MyProfile />} />}
        />

        {/* Default */}
        <Route path="*" element={<Navigate to="/login" />} />
      </Routes>
    </Suspense>
  );

  return (
    <div className="relative min-h-screen bg-white dark:bg-slate-900 transition-colors duration-300 flex flex-col">
      {userAuthenticated && <AuthSessionRefresher />}
      {userAuthenticated && <UserBlockedDialog handleLogout={handleForcedLogout} />}
      {userAuthenticated && (
        <SessionExpiredContainer handleLogout={handleForcedLogout} />
      )}
      {userAuthenticated && <MobileVerificationReminder />}
      {userAuthenticated && showNavbar && <NavbarSelector />}

      <main
        className={`w-full transition-all duration-300 flex-1 overflow-y-auto overflow-x-hidden ${
          userAuthenticated && showNavbar && navLayout === "sidebar"
            ? "app-layout--sidebar"
            : ""
        }`}
        style={{
          paddingTop:
            userAuthenticated && showNavbar && navLayout === "horizontal"
              ? NAVBAR_HEIGHT
              : 0,
          paddingLeft:
            userAuthenticated && showNavbar && navLayout === "sidebar"
              ? "var(--app-sidebar-width, 5rem)"
              : 0,
          position: "relative",
          zIndex: 1,
        }}
      >
        <div className="w-full">{routesContent}</div>
      </main>

      {<Footer />}
    </div>
  );
}